/**
 * Client uploader top-level. Reads the invite_id from the URL path
 * (`/i/<id>`) and the cert fingerprint from the URL fragment
 * (`#<hex>`). Both are passed into the InviteFlow state machine,
 * which verifies the cert before letting the client see anything.
 */

import { t } from "@dovach/i18n";

import { BrowserUnsupported } from "./components/BrowserUnsupported";
import { InviteFlow } from "./components/InviteFlow";
import { TrustFooter } from "./components/TrustFooter";
import { TrustHeader } from "./components/TrustHeader";
import { isBrowserSupported } from "./lib/browser-support";

function readInviteIdFromPath(path: string): string | null {
  // Expected shape: /i/<32-hex-id> [optional trailing slash]
  const m = /^\/i\/([0-9a-fA-F-]+)\/?$/.exec(path);
  if (!m) return null;
  const id = m[1].replace(/-/g, "");
  return id.length === 32 ? id : null;
}

function readFingerprintFromHash(hash: string): string | null {
  // Fragment is `#<40-hex>` per dashboard.create_invite (BLAKE2b-160 of cert blob).
  const m = /^#([0-9a-fA-F]{40})$/.exec(hash);
  return m ? m[1].toLowerCase() : null;
}

export function App() {
  if (!isBrowserSupported()) {
    return <BrowserUnsupported />;
  }

  const inviteId = readInviteIdFromPath(window.location.pathname);
  const fragmentFp = readFingerprintFromHash(window.location.hash);

  return (
    <div className="mx-auto flex min-h-dvh max-w-2xl flex-col px-4 py-6">
      <TrustHeader accountantName="רו״ח שרה כהן" />
      <main className="mt-8 flex-1">
        {inviteId ? (
          <InviteFlow inviteId={inviteId} fragmentFp={fragmentFp} />
        ) : (
          <NoInviteId />
        )}
      </main>
      <TrustFooter />
    </div>
  );
}

function NoInviteId() {
  return (
    <section className="mt-8">
      <h1 className="text-2xl font-semibold">{t("client_web.no_invite_title")}</h1>
      <p className="mt-3 text-slate-600 dark:text-slate-300">
        {t("client_web.no_invite_body")}
      </p>
    </section>
  );
}

// keep `useState` imports tidy for future refactors that may need it.
export const __test_only = { readInviteIdFromPath, readFingerprintFromHash };
